import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { generateKidsStory, speakKidsText } from "@/lib/kids-zone.functions";
import { StoryBooksTab } from "@/components/kids/story-books";
import {
  Sparkles, Volume2, BookOpen, Gamepad2, Star, Trophy, Loader2, RefreshCw,
  Home as HomeIcon, ChevronLeft, ChevronRight, Check, X, Library,
} from "lucide-react";

export const Route = createFileRoute("/learner/kids")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "Kids Zone — MAKAI" },
      { name: "description", content: "A magical learning playground for young learners: stories, games and read-aloud adventures." },
    ],
  }),
  component: KidsZonePage,
});

// ---------------- Avatar & rewards (localStorage) ----------------

type KidsProfile = {
  name: string;
  animal: string;
  color: string;
  coins: number;
  stickers: string[];
};

const ANIMALS: { key: string; emoji: string; label: string }[] = [
  { key: "owl", emoji: "🦉", label: "Owl" },
  { key: "lion", emoji: "🦁", label: "Lion" },
  { key: "elephant", emoji: "🐘", label: "Elephant" },
  { key: "monkey", emoji: "🐵", label: "Monkey" },
  { key: "giraffe", emoji: "🦒", label: "Giraffe" },
  { key: "zebra", emoji: "🦓", label: "Zebra" },
  { key: "fox", emoji: "🦊", label: "Fox" },
  { key: "panda", emoji: "🐼", label: "Panda" },
  { key: "penguin", emoji: "🐧", label: "Penguin" },
  { key: "unicorn", emoji: "🦄", label: "Unicorn" },
];

const COLORS = ["#FDBA74", "#FCA5A5", "#FDE68A", "#86EFAC", "#93C5FD", "#C4B5FD", "#F9A8D4"];

const KEY = "makai.kids.profile.v1";

function loadProfile(): KidsProfile {
  if (typeof window === "undefined") {
    return { name: "", animal: "owl", color: COLORS[0], coins: 0, stickers: [] };
  }
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { name: "", animal: "owl", color: COLORS[0], coins: 0, stickers: [], ...JSON.parse(raw) };
  } catch {}
  return { name: "", animal: "owl", color: COLORS[0], coins: 0, stickers: [] };
}
function saveProfile(p: KidsProfile) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch {}
}

// ---------------- Page ----------------

type Tab = "home" | "story" | "books" | "games" | "read";

function KidsZonePage() {
  const [profile, setProfile] = useState<KidsProfile>(() => loadProfile());
  const [tab, setTab] = useState<Tab>("home");
  const [celebrate, setCelebrate] = useState<string | null>(null);

  useEffect(() => { saveProfile(profile); }, [profile]);

  const addCoins = useCallback((n: number, sticker?: string) => {
    setProfile((p) => ({
      ...p,
      coins: p.coins + n,
      stickers: sticker && !p.stickers.includes(sticker) ? [...p.stickers, sticker] : p.stickers,
    }));
    if (sticker) {
      setCelebrate(sticker);
      setTimeout(() => setCelebrate(null), 1600);
    }
  }, []);

  const animalEmoji = ANIMALS.find((a) => a.key === profile.animal)?.emoji ?? "🦉";

  return (
    <AppShell role="learner">
      <div className="min-h-full" style={{ background: `linear-gradient(180deg, ${profile.color}22, transparent 40%)` }}>
        <div className="mx-auto max-w-4xl px-4 pb-24 pt-4 sm:pt-6">
          {/* Header */}
          <div className="flex items-center justify-between rounded-3xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-4 text-white shadow-lg">
            <div className="flex items-center gap-3">
              <div className="grid h-14 w-14 place-items-center rounded-2xl text-4xl shadow-inner" style={{ background: profile.color }}>
                {animalEmoji}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest opacity-80">Kids Zone</div>
                <div className="text-lg font-black leading-tight">
                  {profile.name ? `Hi, ${profile.name}!` : "Welcome, little explorer!"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/20 px-3 py-1.5 backdrop-blur">
              <Star className="h-5 w-5 fill-yellow-300 text-yellow-300" />
              <span className="text-lg font-black">{profile.coins}</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 grid grid-cols-5 gap-2">
            <TabButton icon={HomeIcon} label="My Zoo" active={tab === "home"} onClick={() => setTab("home")} />
            <TabButton icon={Library} label="Books" active={tab === "books"} onClick={() => setTab("books")} />
            <TabButton icon={BookOpen} label="Stories" active={tab === "story"} onClick={() => setTab("story")} />
            <TabButton icon={Gamepad2} label="Games" active={tab === "games"} onClick={() => setTab("games")} />
            <TabButton icon={Volume2} label="Read" active={tab === "read"} onClick={() => setTab("read")} />
          </div>

          <div className="mt-5">
            {tab === "home" && <HomeTab profile={profile} setProfile={setProfile} />}
            {tab === "books" && <StoryBooksTab />}
            {tab === "story" && <StoryTab profile={profile} addCoins={addCoins} />}
            {tab === "games" && <GamesTab addCoins={addCoins} />}
            {tab === "read" && <ReadAloudTab />}
          </div>
        </div>

        {celebrate && (
          <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center">
            <div className="animate-in zoom-in fade-in rounded-3xl bg-white p-8 text-center shadow-2xl">
              <div className="text-6xl">{celebrate}</div>
              <div className="mt-2 text-lg font-black text-purple-600">New sticker unlocked!</div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TabButton({ icon: Icon, label, active, onClick }: { icon: typeof HomeIcon; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-1 rounded-2xl border-2 py-3 text-xs font-bold transition-all",
        active
          ? "border-purple-500 bg-purple-500 text-white shadow-md scale-105"
          : "border-purple-200 bg-white text-purple-700 hover:bg-purple-50",
      )}
    >
      <Icon className="h-5 w-5" />
      {label}
    </button>
  );
}

// ---------------- Home / avatar ----------------

function HomeTab({ profile, setProfile }: { profile: KidsProfile; setProfile: (fn: (p: KidsProfile) => KidsProfile) => void }) {
  return (
    <div className="space-y-4">
      <Card className="rounded-3xl border-2 border-purple-200 bg-white p-5 shadow-md">
        <div className="text-sm font-bold uppercase tracking-wider text-purple-500">My Name</div>
        <Input
          value={profile.name}
          onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
          placeholder="Type your name…"
          className="mt-2 rounded-2xl border-2 border-purple-200 text-lg font-bold"
          maxLength={20}
        />
      </Card>

      <Card className="rounded-3xl border-2 border-purple-200 bg-white p-5 shadow-md">
        <div className="text-sm font-bold uppercase tracking-wider text-purple-500">Pick your animal friend</div>
        <div className="mt-3 grid grid-cols-5 gap-2">
          {ANIMALS.map((a) => (
            <button
              key={a.key}
              onClick={() => setProfile((p) => ({ ...p, animal: a.key }))}
              className={cn(
                "aspect-square rounded-2xl border-2 text-3xl transition-transform hover:scale-110",
                profile.animal === a.key ? "border-purple-500 bg-purple-100 scale-110" : "border-purple-200 bg-white",
              )}
              title={a.label}
            >
              {a.emoji}
            </button>
          ))}
        </div>
      </Card>

      <Card className="rounded-3xl border-2 border-purple-200 bg-white p-5 shadow-md">
        <div className="text-sm font-bold uppercase tracking-wider text-purple-500">Pick a colour</div>
        <div className="mt-3 flex flex-wrap gap-3">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setProfile((p) => ({ ...p, color: c }))}
              className={cn(
                "h-12 w-12 rounded-full border-4 transition-transform hover:scale-110",
                profile.color === c ? "border-purple-600 scale-110" : "border-white",
              )}
              style={{ background: c }}
            />
          ))}
        </div>
      </Card>

      <Card className="rounded-3xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50 p-5 shadow-md">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-orange-500">
          <Trophy className="h-4 w-4" /> My Sticker Book
        </div>
        {profile.stickers.length === 0 ? (
          <p className="mt-2 text-sm text-orange-700">Play games and finish stories to earn stickers! 🎉</p>
        ) : (
          <div className="mt-3 grid grid-cols-6 gap-2">
            {profile.stickers.map((s, i) => (
              <div key={i} className="grid aspect-square place-items-center rounded-2xl bg-white text-3xl shadow-sm">
                {s}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------- Story Studio ----------------

type Story = { title: string; pages: { text: string; emoji: string }[]; moral: string };

const STORY_PRESETS = [
  "A day at the savannah",
  "Learning to share mangoes",
  "The lost baby elephant",
  "Counting stars at night",
  "A brave little friend",
];

function StoryTab({ profile, addCoins }: { profile: KidsProfile; addCoins: (n: number, sticker?: string) => void }) {
  const gen = useServerFn(generateKidsStory);
  const [theme, setTheme] = useState(STORY_PRESETS[0]);
  const [story, setStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const animal = ANIMALS.find((a) => a.key === profile.animal)?.label ?? "owl";

  const create = async () => {
    setLoading(true); setError(null); setStory(null); setPage(0);
    try {
      const s = await gen({ data: { theme, heroName: profile.name || "Zola", heroAnimal: animal.toLowerCase() } });
      setStory(s);
      addCoins(5);
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };

  const next = () => {
    if (!story) return;
    if (page < story.pages.length - 1) setPage(page + 1);
    else addCoins(10, "📖");
  };

  return (
    <div className="space-y-4">
      {!story && (
        <Card className="rounded-3xl border-2 border-pink-200 bg-white p-5 shadow-md">
          <div className="text-sm font-bold uppercase tracking-wider text-pink-500">Choose an adventure</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {STORY_PRESETS.map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={cn(
                  "rounded-full border-2 px-4 py-2 text-sm font-bold",
                  theme === t ? "border-pink-500 bg-pink-500 text-white" : "border-pink-200 bg-white text-pink-700",
                )}
              >
                {t}
              </button>
            ))}
          </div>
          <Input
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="…or type your own idea"
            className="mt-3 rounded-2xl border-2 border-pink-200"
            maxLength={80}
          />
          <Button
            onClick={create}
            disabled={loading || !theme.trim()}
            className="mt-4 h-14 w-full rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 text-lg font-black shadow-lg hover:opacity-90"
          >
            {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Sparkles className="mr-2 h-5 w-5" />}
            Make my story!
          </Button>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </Card>
      )}

      {story && (
        <StoryReader story={story} page={page} onPrev={() => setPage(Math.max(0, page - 1))} onNext={next} onRestart={() => { setStory(null); setPage(0); }} />
      )}
    </div>
  );
}

function StoryReader({ story, page, onPrev, onNext, onRestart }: { story: Story; page: number; onPrev: () => void; onNext: () => void; onRestart: () => void }) {
  const isLast = page >= story.pages.length - 1;
  const p = story.pages[page];
  return (
    <Card className="overflow-hidden rounded-3xl border-2 border-purple-300 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6 shadow-lg">
      <div className="text-center text-xs font-bold uppercase tracking-widest text-purple-500">{story.title}</div>
      <div className="mt-6 grid place-items-center text-8xl sm:text-9xl">{p.emoji}</div>
      <p className="mt-6 text-center text-xl font-bold leading-relaxed text-slate-800 sm:text-2xl">{p.text}</p>
      <div className="mt-3 flex justify-center">
        <SpeakButton text={p.text} />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={onPrev} disabled={page === 0} className="rounded-2xl border-2 border-purple-200">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex gap-1.5">
          {story.pages.map((_, i) => (
            <div key={i} className={cn("h-2 w-2 rounded-full", i === page ? "bg-purple-500 w-6" : "bg-purple-200")} />
          ))}
        </div>
        <Button onClick={onNext} className="rounded-2xl bg-purple-500 hover:bg-purple-600">
          {isLast ? "Finish 🎉" : <ChevronRight className="h-5 w-5" />}
        </Button>
      </div>

      {isLast && (
        <div className="mt-5 rounded-2xl bg-yellow-100 p-4 text-center">
          <div className="text-xs font-bold uppercase text-orange-500">The lesson</div>
          <p className="mt-1 text-sm font-bold text-orange-800">{story.moral}</p>
        </div>
      )}

      <Button variant="ghost" onClick={onRestart} className="mt-4 w-full rounded-2xl text-purple-600">
        <RefreshCw className="mr-2 h-4 w-4" /> New story
      </Button>
    </Card>
  );
}

// ---------------- Read Aloud ----------------

function SpeakButton({ text }: { text: string }) {
  const speak = useServerFn(speakKidsText);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const play = async () => {
    setLoading(true);
    try {
      const { audioBase64, mime } = await speak({ data: { text } });
      const src = `data:${mime};base64,${audioBase64}`;
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = src;
      await audioRef.current.play();
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={play} disabled={loading} className="rounded-full border-2 border-purple-300 bg-white text-purple-600">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
      <span className="ml-1 text-xs font-bold">Read to me</span>
    </Button>
  );
}

const READ_LESSONS: { title: string; emoji: string; body: string }[] = [
  {
    title: "The Water Cycle",
    emoji: "🌧️",
    body: "The sun warms the water in the lake. The water rises up as tiny mist. High in the sky, the mist makes a cloud. When the cloud is full, rain falls down to the ground. The rain gives us water to drink and helps plants grow.",
  },
  {
    title: "Counting to Ten",
    emoji: "🔢",
    body: "One elephant, two elephants, three elephants stomp. Four birds, five birds, six birds hop. Seven fish, eight fish, nine fish swim. And ten happy monkeys swing on a limb!",
  },
  {
    title: "Colours of the Rainbow",
    emoji: "🌈",
    body: "Red like a ripe tomato. Orange like a juicy mango. Yellow like the bright sun. Green like fresh matoke leaves. Blue like the deep sky. Purple like a sweet grape. All these colours make a rainbow.",
  },
  {
    title: "Kind Words",
    emoji: "💛",
    body: "Say please when you ask. Say thank you when you get. Say sorry when you make a mistake. Say hello with a smile. Kind words are like little sunshine — they make everyone feel warm.",
  },
];

function ReadAloudTab() {
  const [i, setI] = useState(0);
  const lesson = READ_LESSONS[i];
  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {READ_LESSONS.map((l, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            className={cn(
              "flex shrink-0 flex-col items-center gap-1 rounded-2xl border-2 p-3 min-w-[92px]",
              i === idx ? "border-blue-500 bg-blue-500 text-white" : "border-blue-200 bg-white text-blue-700",
            )}
          >
            <span className="text-2xl">{l.emoji}</span>
            <span className="text-xs font-bold">{l.title}</span>
          </button>
        ))}
      </div>
      <Card className="rounded-3xl border-2 border-blue-200 bg-gradient-to-br from-sky-50 to-blue-50 p-6 shadow-md">
        <div className="grid place-items-center text-8xl">{lesson.emoji}</div>
        <h2 className="mt-4 text-center text-2xl font-black text-blue-800">{lesson.title}</h2>
        <p className="mt-4 text-lg leading-relaxed text-slate-800">{lesson.body}</p>
        <div className="mt-4 flex justify-center">
          <SpeakButton text={lesson.body} />
        </div>
      </Card>
    </div>
  );
}

// ---------------- Games ----------------

function GamesTab({ addCoins }: { addCoins: (n: number, sticker?: string) => void }) {
  const [game, setGame] = useState<"menu" | "count" | "add" | "letter" | "spell">("menu");

  if (game === "menu") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <GameTile emoji="🐘" label="Count the Animals" color="from-orange-400 to-pink-500" onClick={() => setGame("count")} />
        <GameTile emoji="➕" label="Add Them Up" color="from-blue-400 to-indigo-500" onClick={() => setGame("add")} />
        <GameTile emoji="🔤" label="Find the Letter" color="from-green-400 to-emerald-500" onClick={() => setGame("letter")} />
        <GameTile emoji="🐝" label="Spell the Word" color="from-yellow-400 to-orange-500" onClick={() => setGame("spell")} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Button variant="ghost" onClick={() => setGame("menu")} className="rounded-2xl text-purple-600">
        <ChevronLeft className="mr-1 h-4 w-4" /> Back to games
      </Button>
      {game === "count" && <CountGame addCoins={addCoins} />}
      {game === "add" && <AddGame addCoins={addCoins} />}
      {game === "letter" && <LetterGame addCoins={addCoins} />}
      {game === "spell" && <SpellGame addCoins={addCoins} />}
    </div>
  );
}

function GameTile({ emoji, label, color, onClick }: { emoji: string; label: string; color: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn("group aspect-square rounded-3xl bg-gradient-to-br p-4 text-white shadow-lg transition-transform hover:scale-105", color)}
    >
      <div className="text-6xl transition-transform group-hover:scale-110">{emoji}</div>
      <div className="mt-2 text-sm font-black leading-tight">{label}</div>
    </button>
  );
}

function useGameRound(deps: unknown[]) {
  const [feedback, setFeedback] = useState<"correct" | "wrong" | null>(null);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const next = useCallback(() => { setFeedback(null); setRound((r) => r + 1); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFeedback(null); }, deps);
  return { feedback, setFeedback, score, setScore, round, next };
}

function GameShell({ title, children, score, feedback }: { title: string; children: React.ReactNode; score: number; feedback: "correct" | "wrong" | null }) {
  return (
    <Card className="relative overflow-hidden rounded-3xl border-2 border-purple-200 bg-white p-5 shadow-md">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black uppercase tracking-wider text-purple-500">{title}</div>
        <div className="flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-yellow-700">
          <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" /> <span className="font-black">{score}</span>
        </div>
      </div>
      <div className="mt-4">{children}</div>
      {feedback && (
        <div className={cn(
          "pointer-events-none absolute inset-0 grid place-items-center",
          feedback === "correct" ? "bg-green-500/20" : "bg-red-500/10",
        )}>
          <div className={cn("rounded-full p-6 shadow-2xl", feedback === "correct" ? "bg-green-500" : "bg-red-400")}>
            {feedback === "correct" ? <Check className="h-12 w-12 text-white" /> : <X className="h-12 w-12 text-white" />}
          </div>
        </div>
      )}
    </Card>
  );
}

function CountGame({ addCoins }: { addCoins: (n: number, sticker?: string) => void }) {
  const g = useGameRound([]);
  const { feedback, setFeedback, score, setScore, round, next } = g;
  const { count, animal, options } = useMemo(() => {
    const c = 1 + Math.floor(Math.random() * 8);
    const a = ANIMALS[Math.floor(Math.random() * ANIMALS.length)].emoji;
    const set = new Set<number>([c]);
    while (set.size < 4) set.add(1 + Math.floor(Math.random() * 9));
    const opts = Array.from(set).sort(() => Math.random() - 0.5);
    return { count: c, animal: a, options: opts };
  }, [round]);

  const pick = (n: number) => {
    if (feedback) return;
    if (n === count) {
      setFeedback("correct");
      const nextScore = score + 1;
      setScore(nextScore);
      addCoins(2);
      if (nextScore === 5) addCoins(0, "🐘");
      setTimeout(next, 900);
    } else {
      setFeedback("wrong");
      setTimeout(() => setFeedback(null), 700);
    }
  };

  return (
    <GameShell title="Count the animals!" score={score} feedback={feedback}>
      <div className="grid min-h-[120px] place-items-center rounded-2xl bg-orange-50 p-4 text-4xl leading-tight">
        {Array.from({ length: count }).map((_, i) => <span key={i} className="mx-0.5 inline-block">{animal}</span>)}
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {options.map((n) => (
          <Button key={n} onClick={() => pick(n)} className="h-16 rounded-2xl bg-orange-500 text-2xl font-black hover:bg-orange-600">{n}</Button>
        ))}
      </div>
    </GameShell>
  );
}

function AddGame({ addCoins }: { addCoins: (n: number, sticker?: string) => void }) {
  const g = useGameRound([]);
  const { feedback, setFeedback, score, setScore, round, next } = g;
  const { a, b, options, answer } = useMemo(() => {
    const a = 1 + Math.floor(Math.random() * 5);
    const b = 1 + Math.floor(Math.random() * 5);
    const ans = a + b;
    const set = new Set<number>([ans]);
    while (set.size < 4) set.add(Math.max(2, ans + Math.floor(Math.random() * 5) - 2 + (Math.random() < 0.5 ? -1 : 1)));
    return { a, b, answer: ans, options: Array.from(set).sort(() => Math.random() - 0.5) };
  }, [round]);

  const pick = (n: number) => {
    if (feedback) return;
    if (n === answer) {
      setFeedback("correct"); const ns = score + 1; setScore(ns); addCoins(3);
      if (ns === 5) addCoins(0, "➕");
      setTimeout(next, 900);
    } else {
      setFeedback("wrong");
      setTimeout(() => setFeedback(null), 700);
    }
  };

  return (
    <GameShell title="Add them up!" score={score} feedback={feedback}>
      <div className="grid place-items-center rounded-2xl bg-blue-50 p-6">
        <div className="text-5xl font-black text-blue-700">{a} + {b} = ?</div>
        <div className="mt-3 text-3xl">
          {"🍎".repeat(a)} <span className="mx-2 text-blue-400">+</span> {"🍌".repeat(b)}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {options.map((n) => (
          <Button key={n} onClick={() => pick(n)} className="h-16 rounded-2xl bg-blue-500 text-2xl font-black hover:bg-blue-600">{n}</Button>
        ))}
      </div>
    </GameShell>
  );
}

function LetterGame({ addCoins }: { addCoins: (n: number, sticker?: string) => void }) {
  const g = useGameRound([]);
  const { feedback, setFeedback, score, setScore, round, next } = g;
  const { target, options } = useMemo(() => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const t = letters[Math.floor(Math.random() * letters.length)];
    const set = new Set<string>([t]);
    while (set.size < 6) set.add(letters[Math.floor(Math.random() * letters.length)]);
    return { target: t, options: Array.from(set).sort(() => Math.random() - 0.5) };
  }, [round]);

  const pick = (l: string) => {
    if (feedback) return;
    if (l === target) {
      setFeedback("correct"); const ns = score + 1; setScore(ns); addCoins(2);
      if (ns === 5) addCoins(0, "🔤");
      setTimeout(next, 900);
    } else {
      setFeedback("wrong"); setTimeout(() => setFeedback(null), 700);
    }
  };

  return (
    <GameShell title={`Find the letter ${target}`} score={score} feedback={feedback}>
      <div className="grid place-items-center rounded-2xl bg-emerald-50 p-6">
        <div className="text-8xl font-black text-emerald-600">{target}</div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {options.map((l) => (
          <Button key={l} onClick={() => pick(l)} className="h-16 rounded-2xl bg-emerald-500 text-3xl font-black hover:bg-emerald-600">{l}</Button>
        ))}
      </div>
    </GameShell>
  );
}

const SPELL_WORDS: { word: string; emoji: string }[] = [
  { word: "CAT", emoji: "🐱" }, { word: "DOG", emoji: "🐶" }, { word: "SUN", emoji: "☀️" },
  { word: "BEE", emoji: "🐝" }, { word: "FISH", emoji: "🐟" }, { word: "STAR", emoji: "⭐" },
  { word: "TREE", emoji: "🌳" }, { word: "MOON", emoji: "🌙" }, { word: "BIRD", emoji: "🐦" },
];

function SpellGame({ addCoins }: { addCoins: (n: number, sticker?: string) => void }) {
  const g = useGameRound([]);
  const { feedback, setFeedback, score, setScore, round, next } = g;
  const [picked, setPicked] = useState<string[]>([]);
  const { word, emoji, letters } = useMemo(() => {
    const w = SPELL_WORDS[Math.floor(Math.random() * SPELL_WORDS.length)];
    const extra = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").filter((l) => !w.word.includes(l));
    const distract = extra.sort(() => Math.random() - 0.5).slice(0, 3);
    const all = [...w.word.split(""), ...distract].sort(() => Math.random() - 0.5);
    return { word: w.word, emoji: w.emoji, letters: all };
  }, [round]);

  useEffect(() => { setPicked([]); }, [round]);

  const pick = (l: string, i: number) => {
    if (feedback) return;
    const target = word[picked.length];
    if (l === target) {
      const np = [...picked, l];
      setPicked(np);
      if (np.length === word.length) {
        setFeedback("correct"); const ns = score + 1; setScore(ns); addCoins(4);
        if (ns === 3) addCoins(0, "🐝");
        setTimeout(next, 1000);
      }
    } else {
      setFeedback("wrong"); setTimeout(() => setFeedback(null), 700);
    }
    void i;
  };

  return (
    <GameShell title="Spell the word!" score={score} feedback={feedback}>
      <div className="grid place-items-center rounded-2xl bg-yellow-50 p-4">
        <div className="text-7xl">{emoji}</div>
        <div className="mt-3 flex gap-2">
          {word.split("").map((l, i) => (
            <div key={i} className={cn(
              "grid h-14 w-12 place-items-center rounded-xl border-2 text-3xl font-black",
              i < picked.length ? "border-yellow-500 bg-yellow-400 text-white" : "border-yellow-300 bg-white text-yellow-300",
            )}>
              {i < picked.length ? picked[i] : "_"}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {letters.map((l, i) => (
          <Button key={i} onClick={() => pick(l, i)} className="h-14 w-14 rounded-2xl bg-yellow-500 text-2xl font-black hover:bg-yellow-600">{l}</Button>
        ))}
      </div>
    </GameShell>
  );
}
